import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { CollapseMenu } from 'components/UIElements'
import CycleDetailContent from './CycleDetailContent'
import VisitDetailContent from './VisitDetailContent'
import InstrumentDetailContent from './InstrumentDetailContent'
import {
  getActiveScheduleIndicesByInstrumentId,
  getUniqueInstrumentsForDay,
} from '../utils/studyTimelineScheduleDisplay'
import { DAY, FALLBACKS, INSTRUMENT_TYPE_COLORS, LABELS, MESSAGES } from '../utils/constants'

const StudyDayDetails = ({
  selectedDay,
  dayItems,
  dayCycleRanges,
  studyID,
  studyVersionID,
  visitIdToName = {},
  instrumentIdToName = {},
  useAbsoluteStartDate = false,
  absoluteStartDate = null,
  onEditSchedule,
  currentStudy,
  studyDurationDays = null,
  scheduleAnchorContext = {},
}) => {
  const [allExpanded, setAllExpanded] = useState(false)

  const activeScheduleIndicesByInstrument = useMemo(
    () => getActiveScheduleIndicesByInstrumentId(dayItems.instruments),
    [dayItems.instruments],
  )

  // One drawer card per instrument; all schedules for that day are listed inside.
  const uniqueInstruments = useMemo(
    () => getUniqueInstrumentsForDay(dayItems.instruments),
    [dayItems.instruments],
  )

  const hasAnyItems =
    dayCycleRanges.length > 0 || dayItems.visits.length > 0 || uniqueInstruments.length > 0

  // Reset expand state when selectedDay changes
  useEffect(() => {
    setAllExpanded(false)
  }, [selectedDay])

  const toggleAll = () => {
    setAllExpanded(prev => !prev)
  }

  return (
    <div className='study-timeline-day-details'>
      <div className='study-timeline-day-header'>
        <h3>{DAY.LABEL_PREFIX}{selectedDay}</h3>
        {hasAnyItems && (
          <button
            type='button'
            onClick={toggleAll}
            className='study-timeline-expand-collapse-all'
            title={allExpanded ? LABELS.COLLAPSE_ALL : LABELS.EXPAND_ALL}
          >
            <i className={`fas fa-caret-${allExpanded ? 'down' : 'right'}`} />
          </button>
        )}
      </div>
      {hasAnyItems ? (
        <>
          {/* Cycle Ranges */}
          {dayCycleRanges.map(range => (
            <CollapseMenu
              key={range.cycleId}
              titleContent={range.cycleName || FALLBACKS.CYCLE}
              menuContent={<CycleDetailContent range={range} selectedDay={selectedDay} />}
              className='study-timeline-detail-section'
              isOpen={allExpanded}
            />
          ))}

          {/* Visits */}
          {dayItems.visits.map(visit => (
            <CollapseMenu
              key={visit.id}
              titleContent={visit.label || FALLBACKS.VISIT}
              menuContent={<VisitDetailContent visit={visit} selectedDay={selectedDay} />}
              className='study-timeline-detail-section'
              isOpen={allExpanded}
            />
          ))}

          {/* Instruments */}
          {uniqueInstruments.map(instrument => {
            const instrumentData = instrument.instrumentData || {}
            const instrumentType = (instrumentData.type || '').toUpperCase()
            const instrumentTypeClass =
              instrumentType && INSTRUMENT_TYPE_COLORS[instrumentType]
                ? `instrument-type-${instrumentType.toLowerCase()}`
                : ''
            const instrumentName =
              instrumentData.metadata?.display_name ||
              instrumentData.title ||
              instrumentData.name ||
              instrument.label ||
              FALLBACKS.INSTRUMENT
            const instrumentKey = String(instrument.instrumentId ?? instrumentData.id ?? instrument.id)
            const activeScheduleIndices = activeScheduleIndicesByInstrument.get(instrumentKey) || new Set()
            const menuContent = (
              <InstrumentDetailContent
                instrument={instrument}
                selectedDay={selectedDay}
                studyID={studyID}
                studyVersionID={studyVersionID}
                visitIdToName={visitIdToName}
                instrumentIdToName={instrumentIdToName}
                useAbsoluteStartDate={useAbsoluteStartDate}
                absoluteStartDate={absoluteStartDate}
                onEditSchedule={onEditSchedule}
                activeScheduleIndices={activeScheduleIndices}
                currentStudy={currentStudy}
                studyDurationDays={studyDurationDays}
                scheduleAnchorContext={scheduleAnchorContext}
              />
            )
            return (
              <CollapseMenu
                key={instrumentKey}
                titleContent={instrumentName}
                menuContent={menuContent}
                className={`study-timeline-detail-section ${instrumentTypeClass}`.trim()}
                isOpen={allExpanded}
              />
            )
          })}
        </>
      ) : (
        <p className='study-timeline-no-event'>{MESSAGES.NO_EVENTS_THIS_DAY}</p>
      )}
    </div>
  )
}

StudyDayDetails.propTypes = {
  selectedDay: PropTypes.number.isRequired,
  dayItems: PropTypes.shape({
    cycles: PropTypes.array,
    visits: PropTypes.array,
    instruments: PropTypes.array,
  }),
  dayCycleRanges: PropTypes.array,
  studyID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  studyVersionID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  visitIdToName: PropTypes.object,
  instrumentIdToName: PropTypes.object,
  useAbsoluteStartDate: PropTypes.bool,
  absoluteStartDate: PropTypes.string,
  onEditSchedule: PropTypes.func,
  currentStudy: PropTypes.object,
  studyDurationDays: PropTypes.number,
  scheduleAnchorContext: PropTypes.object,
}

export default StudyDayDetails

